/**
 * Sistema de Regalos Automáticos por Cupón — lógica unificada
 * ============================================================
 * Fuente única compartida por el cart drawer (todas las páginas ≠ cart)
 * y la página /cart. La configuración se inyecta antes vía
 * `snippets/gift-config.liquid` en: window.discountGifts, window.giftMessages,
 * window.giftSettings.
 *
 * Reemplaza el JS inline duplicado que vivía en sections/cart-drawer.liquid
 * y sections/cart.liquid.
 *
 * Propiedades del line item de regalo:
 *   _gift: 'true' · _discount_code: <CÓDIGO> · _gift_name: <título>
 */
(function () {
  'use strict';

  var GIFTS = window.discountGifts || {};
  var MSG = window.giftMessages || {};
  var SETTINGS = window.giftSettings || {};
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Contexto: la página /cart usa recarga completa; el drawer refresca secciones.
  var IS_CART_PAGE = !!document.querySelector('[data-cart-page]');

  // ============================================================
  // Utilidades
  // ============================================================

  function formatMoney(cents) {
    if (window.theme && typeof window.theme.formatMoney === 'function') {
      return window.theme.formatMoney(cents, SETTINGS.moneyFormat);
    }
    return 'S/ ' + (cents / 100).toFixed(2);
  }

  // Igual que el filtro Liquid money_without_trailing_zeros (quita el ,00 / .00 final).
  function moneyNoZeros(cents) {
    return formatMoney(cents).replace(/[.,]0+(\D*)$/, '$1');
  }

  // Escapa texto para insertarlo de forma segura vía innerHTML.
  function escHtml(s) {
    return String(s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  // Barra de progreso de regalo por umbral (mini-carrito). Actualiza texto/ancho
  // según el total del carrito. No hace nada si el bloque no está presente.
  // Subtotal que cuenta para el umbral: si el bloque define una colección
  // (data-gift-collection), suma SOLO los items de esa colección (sin regalos);
  // si no, usa el subtotal del carrito. Debe coincidir con la condición del
  // descuento automático de Shopify.
  async function qualifyingSubtotalFor(el, cart) {
    var coll = el && el.getAttribute('data-gift-collection');
    if (!coll) return subtotalWithoutGifts(cart);
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (isGiftItem(it)) continue;
      if (await itemBelongsToAnyConfiguredCollection(it.handle, [coll])) {
        total += (it.final_line_price != null ? it.final_line_price : it.line_price);
      }
    }
    return total;
  }

  async function updateGiftProgress(cart) {
    if (!cart) return;
    var els = document.querySelectorAll('[data-gift-progress]');
    for (var i = 0; i < els.length; i++) {
      var qsub = await qualifyingSubtotalFor(els[i], cart);
      updateGiftProgressEl(els[i], cart, qsub);
    }
    // Selector dentro del line item + resaltar el color actualmente en el carrito.
    renderThresholdChooserInLine();
    if (cart && cart.items) {
      var tg = cart.items.filter(isThresholdGiftItem)[0];
      markSelectedThresholdOption(tg ? tg.variant_id : null);
    }
  }

  function updateGiftProgressEl(el, cart, total) {
    var threshold = parseInt(el.getAttribute('data-gift-threshold'), 10) || 0;
    var giftName = el.getAttribute('data-gift-name') || '';
    var thresholdDisplay = el.getAttribute('data-gift-threshold-display') || moneyNoZeros(threshold);
    if (total == null) total = cart.total_price || 0;

    var percent = threshold > 0 ? Math.min(100, Math.round((total * 100) / threshold)) : 0;
    var remaining = Math.max(0, threshold - total);

    var collTitle = el.getAttribute('data-gift-collection-title') || '';
    var collUrl = el.getAttribute('data-gift-collection-url') || '';
    var collFrag = collTitle
      ? ' en <a class="gift-progress__coll" href="' + escHtml(collUrl) + '">' + escHtml(collTitle) + '</a>'
      : '';

    var state, status;
    if (threshold > 0 && total >= threshold) {
      state = 'complete';
    } else if (total > 0) {
      state = 'partial';
      status = 'Te faltan <span class="gift-progress__amount">' + escHtml(moneyNoZeros(remaining)) + '</span>' + collFrag + ' para tu regalo';
    } else {
      state = 'empty';
      status = 'Por compras mayores a <span class="gift-progress__amount">' + escHtml(thresholdDisplay) + '</span>' + collFrag + ' llévate un ' + escHtml(giftName);
    }

    el.setAttribute('data-gift-state', state);

    var fill = el.querySelector('[data-gift-progress-fill]');
    if (fill) fill.style.width = percent + '%';

    var track = el.querySelector('[role="progressbar"]');
    if (track) track.setAttribute('aria-valuenow', percent);

    var statusEl = el.querySelector('[data-gift-progress-status]');
    if (statusEl && status) statusEl.innerHTML = status;

    // Confeti: al aparecer el estado completo, activar .is-success (una vez).
    // Doble rAF para que el bloque completo se pinte antes de disparar la transición.
    if (state === 'complete') {
      if (!el.classList.contains('is-success')) {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { el.classList.add('is-success'); });
        });
      }
    } else {
      el.classList.remove('is-success');
    }
  }

  // Registra el regalo como atributo del carrito (aparece en el pedido y checkout)
  // cuando el total alcanza el umbral. Lo quita si baja. El color del neceser es
  // aleatorio, por eso NO se agrega un producto: solo queda la nota para despacho.
  var GIFT_ATTR_KEY = 'Regalo';
  async function syncGiftAttribute(cart) {
    var el = document.querySelector('[data-gift-progress]');
    if (!el || !cart) return;

    var threshold = parseInt(el.getAttribute('data-gift-threshold'), 10) || 0;
    if (threshold <= 0) return;

    var giftName = el.getAttribute('data-gift-name') || '';
    var thresholdDisplay = el.getAttribute('data-gift-threshold-display') || '';
    var total = cart.total_price || 0;

    var desired = total >= threshold
      ? giftName + ' (por compra +' + thresholdDisplay + ')'
      : '';

    var current = (cart.attributes && cart.attributes[GIFT_ATTR_KEY]) || '';
    if (current === desired) return; // sin cambios -> no llamar API (evita bucles)

    var attrs = {};
    attrs[GIFT_ATTR_KEY] = desired; // '' elimina el atributo
    try {
      await fetch(ROOT + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ attributes: attrs })
      });
      invalidateCart();
    } catch (e) {
      /* noop */
    }
  }

  // ============================================================
  // Regalo por UMBRAL con SELECCIÓN de producto (Neceser)
  // El cliente elige entre los productos configurados en el bloque (selector).
  // Se agrega como line item con _gift + _gift_threshold (SIN _discount_code, para
  // que validateGiftEligibility / reconcileAutomaticGifts / normalizeCartGifts NO
  // lo toquen). Auto-agrega el primero en stock y permite cambiarlo.
  // ============================================================
  var THRESHOLD_CHOICE_KEY = 'threshold_gift_choice';

  // Opciones del regalo por umbral: fuente única = window.thresholdGiftData
  // (lo expone snippets/gift-progress-bar.liquid desde el setting del bloque).
  function readThresholdOptions() {
    var data = window.thresholdGiftData;
    if (!data || !data.products) return [];
    return data.products.map(function (p) {
      return { variantId: p.variantId, title: p.title || '', available: !!p.available };
    });
  }

  // Nombre de color a mostrar (usa el título de variante; si es 'Default Title'
  // cae al título del producto).
  function colorOf(p) {
    return (p.color && p.color !== 'Default Title') ? p.color : (p.title || '');
  }

  // Dibuja el SELECT VISUAL de color DENTRO del line item del regalo (placeholder
  // [data-gift-threshold-chooser] que rinde cart-line-items.liquid). Idempotente.
  function renderThresholdChooserInLine() {
    var data = window.thresholdGiftData;
    if (!data || !data.products) return;

    var nameEls = document.querySelectorAll('[data-gift-threshold-name]');
    for (var n = 0; n < nameEls.length; n++) {
      if (data.name) nameEls[n].textContent = data.name;
    }

    var pickers = document.querySelectorAll('[data-gift-threshold-chooser]');
    if (!pickers.length) return;

    var opts = data.products.map(function (p) {
      var out = !p.available;
      var c = escHtml(colorOf(p));
      return '<button type="button" class="gt-select__opt gift-choose' + (out ? ' gt-select__opt--out' : '') + '"' +
        ' data-gift-choose data-variant-id="' + p.variantId + '"' +
        ' data-available="' + (out ? 'false' : 'true') + '"' +
        (out ? ' disabled aria-disabled="true"' : '') + '>' +
        (p.image ? '<img src="' + escHtml(p.image) + '" alt="' + c + '" loading="lazy">' : '') +
        '<span>' + c + '</span>' +
        (out ? '<span class="gt-select__badge">Agotado</span>' : '') +
        '</button>';
    }).join('');

    var html =
      '<span class="gt-select__label">Elige el color</span>' +
      '<div class="gt-select" data-gt-select>' +
        '<button type="button" class="gt-select__toggle" data-gt-select-toggle aria-expanded="false">' +
          '<span class="gt-select__current" data-gt-select-current></span>' +
          '<span class="gt-select__caret">&#9662;</span>' +
        '</button>' +
        '<div class="gt-select__menu" data-gt-select-menu hidden>' + opts + '</div>' +
      '</div>';

    for (var g = 0; g < pickers.length; g++) pickers[g].innerHTML = html;
  }

  function isThresholdGiftItem(item) {
    return !!(item.properties && item.properties._gift_threshold === 'true');
  }

  // Producto deseado: la elección guardada si sigue en stock; si no, el primero en stock.
  function pickThresholdVariant(options) {
    var inStock = options.filter(function (o) { return o.available; });
    if (!inStock.length) return null;
    var saved = parseInt(localStorage.getItem(THRESHOLD_CHOICE_KEY) || '0', 10);
    if (saved) {
      for (var i = 0; i < inStock.length; i++) {
        if (inStock[i].variantId === saved) return inStock[i];
      }
    }
    return inStock[0];
  }

  function markSelectedThresholdOption(variantId) {
    var opts = document.querySelectorAll('[data-gift-choose]');
    for (var i = 0; i < opts.length; i++) {
      var v = parseInt(opts[i].getAttribute('data-variant-id'), 10);
      opts[i].classList.toggle('is-selected', !!variantId && v === variantId);
    }
    // Reflejar el color elegido en el "valor" del select (toggle).
    var data = window.thresholdGiftData;
    var sel = null;
    if (data && data.products && variantId) {
      for (var j = 0; j < data.products.length; j++) {
        if (data.products[j].variantId === variantId) { sel = data.products[j]; break; }
      }
    }
    var currents = document.querySelectorAll('[data-gt-select-current]');
    for (var k = 0; k < currents.length; k++) {
      if (sel) {
        var col = escHtml(colorOf(sel));
        currents[k].innerHTML = (sel.image ? '<img src="' + escHtml(sel.image) + '" alt="' + col + '">' : '') + '<span>' + col + '</span>';
      } else {
        currents[k].textContent = '';
      }
    }
  }

  async function applyThresholdCartOps(updates, adds) {
    var lastData = null;
    if (Object.keys(updates).length) {
      var ru = await cartPost(ROOT + 'cart/update.js', { updates: updates, sections: 'api-cart-items' });
      lastData = await ru.json();
    }
    if (adds.length) {
      var ra = await cartPost(ROOT + 'cart/add.js', { items: adds, sections: 'api-cart-items' });
      lastData = await ra.json();
    }
    invalidateCart();
    if (IS_CART_PAGE) { window.location.reload(); return; }
    if (lastData) applyCartResponse(lastData);
  }

  var isReconcilingThreshold = false;
  // Quita del carrito cualquier línea de regalo por umbral (cleanup).
  async function clearThresholdGifts(current) {
    if (!current || !current.length || isReconcilingThreshold) return;
    var rem = {};
    current.forEach(function (it) { rem[it.key] = 0; });
    isReconcilingThreshold = true;
    try { await applyThresholdCartOps(rem, []); } finally { isReconcilingThreshold = false; }
    markSelectedThresholdOption(null);
  }

  async function reconcileThresholdGift(cart) {
    if (!cart) return;
    var el = document.querySelector('[data-gift-progress]');
    var current = cart.items.filter(isThresholdGiftItem);

    // Bloque desactivado o ausente -> quitar cualquier regalo por umbral que quedó pegado.
    if (!el) {
      await clearThresholdGifts(current);
      return;
    }

    var threshold = parseInt(el.getAttribute('data-gift-threshold'), 10) || 0;
    var options = readThresholdOptions();

    // Sin productos configurados -> no auto-agregar; limpiar y usar el atributo legado.
    if (threshold <= 0 || options.length === 0) {
      await clearThresholdGifts(current);
      await syncGiftAttribute(cart);
      return;
    }
    if (isReconcilingThreshold) return;

    var subtotal = await qualifyingSubtotalFor(el, cart); // solo colección (si aplica); el regalo no cuenta
    var qualifies = subtotal >= threshold;

    // Migración: limpia el atributo legado 'Regalo' si quedó de la versión anterior.
    if (cart.attributes && cart.attributes[GIFT_ATTR_KEY]) {
      try {
        var a = {}; a[GIFT_ATTR_KEY] = '';
        await fetch(ROOT + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ attributes: a })
        });
        invalidateCart();
      } catch (e) { /* noop */ }
    }

    // No califica (o nada en stock) -> quitar el regalo por umbral.
    var desired = qualifies ? pickThresholdVariant(options) : null;
    if (!desired) {
      if (current.length) {
        var rem = {};
        current.forEach(function (it) { rem[it.key] = 0; });
        isReconcilingThreshold = true;
        try { await applyThresholdCartOps(rem, []); } finally { isReconcilingThreshold = false; }
      }
      markSelectedThresholdOption(null);
      return;
    }

    // Asegurar exactamente 1 línea de la variante deseada.
    var updates = {};
    var keep = null;
    current.forEach(function (it) {
      if (!keep && it.variant_id === desired.variantId) keep = it;
      else updates[it.key] = 0; // sobra: variante distinta o duplicado
    });
    if (keep && keep.quantity !== 1) updates[keep.key] = 1;

    var adds = [];
    if (!keep) {
      adds.push({
        id: desired.variantId,
        quantity: 1,
        properties: { _gift: 'true', _gift_threshold: 'true', _gift_name: desired.title }
      });
    }

    if (Object.keys(updates).length === 0 && adds.length === 0) {
      markSelectedThresholdOption(desired.variantId);
      return; // ya está correcto
    }

    isReconcilingThreshold = true;
    try {
      await applyThresholdCartOps(updates, adds);
      markSelectedThresholdOption(desired.variantId);
    } finally {
      isReconcilingThreshold = false;
    }
  }

  // El cliente elige un color (opción en stock).
  async function chooseThresholdGift(variantId) {
    if (!variantId) return;
    localStorage.setItem(THRESHOLD_CHOICE_KEY, String(variantId));
    markSelectedThresholdOption(variantId); // feedback inmediato
    await reconcileThresholdGift(await getCart(true));
  }

  // Cache del carrito por ciclo (evita el N+1 de fetch cart.js).
  var _cartCache = null;
  async function getCart(force) {
    if (!force && _cartCache) return _cartCache;
    var res = await fetch(ROOT + 'cart.js');
    _cartCache = await res.json();
    return _cartCache;
  }
  function invalidateCart() {
    _cartCache = null;
  }

  // POST a endpoints del carrito con reintento ante 503/429/5xx o fallo de red
  // (el proxy de `shopify theme dev` a veces devuelve 503 en cart/add).
  async function cartPost(url, body, tries) {
    tries = tries == null ? 2 : tries;
    for (var i = 0; i <= tries; i++) {
      try {
        var res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body)
        });
        if ((res.status === 503 || res.status === 429 || res.status >= 500) && i < tries) {
          await new Promise(function (r) { setTimeout(r, 400 * (i + 1)); });
          continue;
        }
        return res;
      } catch (e) {
        if (i < tries) {
          await new Promise(function (r) { setTimeout(r, 400 * (i + 1)); });
          continue;
        }
        throw e;
      }
    }
  }

  function isGiftItem(item) {
    return !!(item.properties && item.properties._gift === 'true');
  }

  function subtotalWithoutGifts(cart) {
    return cart.items.reduce(function (total, item) {
      if (isGiftItem(item)) return total;
      return total + (item.final_line_price || item.line_price);
    }, 0);
  }

  function itemCountWithoutGifts(cart) {
    return cart.items.reduce(function (total, item) {
      if (isGiftItem(item)) return total;
      return total + item.quantity;
    }, 0);
  }

  function giftsInCart(cart) {
    return cart.items.filter(isGiftItem);
  }

  function itemMatchesAnySpecificProduct(itemHandle, productHandles) {
    if (!Array.isArray(productHandles) || productHandles.length === 0) return false;
    var handle = String(itemHandle || '').toLowerCase().trim();
    return productHandles.some(function (p) {
      return String(p || '').toLowerCase().trim() === handle;
    });
  }

  // Cache de handles de productos por colección (por carga de página).
  var collectionProductCache = {};
  async function itemBelongsToAnyConfiguredCollection(itemHandle, collectionHandles) {
    var productHandle = String(itemHandle || '').toLowerCase().trim();
    if (!productHandle || !Array.isArray(collectionHandles) || collectionHandles.length === 0) {
      return false;
    }
    for (var i = 0; i < collectionHandles.length; i++) {
      var collectionHandle = String(collectionHandles[i] || '').toLowerCase().trim();
      if (!collectionHandle) continue;

      if (!collectionProductCache[collectionHandle]) {
        try {
          var response = await fetch(ROOT + 'collections/' + collectionHandle + '/products.json?limit=250');
          var data = await response.json();
          collectionProductCache[collectionHandle] = (data.products || []).map(function (p) {
            return p.handle.toLowerCase();
          });
        } catch (e) {
          collectionProductCache[collectionHandle] = [];
        }
      }

      if (collectionProductCache[collectionHandle].indexOf(productHandle) !== -1) {
        return true;
      }
    }
    return false;
  }

  function canAddGift(giftConfig) {
    if (!giftConfig) return false;
    if (giftConfig.inventoryPolicy === 'continue') return true;
    return giftConfig.availableForSale && giftConfig.inventoryQuantity > 0;
  }

  // Rotación de productos de regalo (persistida en localStorage).
  function getNextGiftProduct(products, discountCode) {
    if (!products || products.length === 0) return null;
    if (products.length === 1) return products[0];

    var storageKey = 'gift_rotation_' + discountCode;
    var lastIndex = parseInt(localStorage.getItem(storageKey) || '-1', 10);
    var nextIndex = (lastIndex + 1) % products.length;
    var attempts = 0;

    while (attempts < products.length) {
      var product = products[nextIndex];
      if (canAddGift(product)) {
        localStorage.setItem(storageKey, String(nextIndex));
        return product;
      }
      nextIndex = (nextIndex + 1) % products.length;
      attempts++;
    }
    localStorage.setItem(storageKey, String(nextIndex));
    return products[nextIndex];
  }

  // ¿El carrito (ya cargado) sigue calificando para este regalo?
  async function cartQualifies(cart, giftConfig) {
    var subtotal = subtotalWithoutGifts(cart);
    if (giftConfig.minPurchaseAmount > 0 && subtotal < giftConfig.minPurchaseAmount) {
      return { ok: false, reason: 'min_amount', remaining: giftConfig.minPurchaseAmount - subtotal };
    }

    var totalItems = itemCountWithoutGifts(cart);
    if (giftConfig.minItemsQuantity > 0 && totalItems < giftConfig.minItemsQuantity) {
      return { ok: false, reason: 'min_quantity', remaining: giftConfig.minItemsQuantity - totalItems };
    }

    if (giftConfig.categories && giftConfig.categories.length > 0) {
      var found = false;
      for (var i = 0; i < cart.items.length; i++) {
        if (isGiftItem(cart.items[i])) continue;
        if (await itemBelongsToAnyConfiguredCollection(cart.items[i].handle, giftConfig.categories)) {
          found = true;
          break;
        }
      }
      if (!found) return { ok: false, reason: 'category' };
    }

    if (giftConfig.productosEspecificos && giftConfig.productosEspecificos.length > 0) {
      var match = cart.items.some(function (item) {
        return !isGiftItem(item) && itemMatchesAnySpecificProduct(item.handle, giftConfig.productosEspecificos);
      });
      if (!match) return { ok: false, reason: 'specific_product' };
    }

    return { ok: true };
  }

  // ============================================================
  // Mensajes al usuario (input de cupón — solo drawer)
  // ============================================================

  function showDiscountMessage(text, type) {
    var message = document.querySelector('[data-discount-message]');
    if (!message) return;
    message.textContent = text;
    message.style.display = 'block';
    message.classList.remove(
      'cart__discount__message--error',
      'cart__discount__message--success',
      'cart__discount__message--warning'
    );
    message.classList.add('cart__discount__message--' + (type || 'success'));
  }

  // ============================================================
  // Sincronización del precio del botón de checkout (lee del DOM)
  // ============================================================

  function syncCheckoutPrice() {
    var checkoutPriceElement = document.querySelector('[data-cart-checkout-price]');
    var cartTotalElement = document.querySelector('[data-cart-total]');
    if (!checkoutPriceElement || !cartTotalElement) return;

    var cartSubtotal = parseInt(cartTotalElement.getAttribute('data-cart-total'), 10) || 0;
    checkoutPriceElement.textContent = formatMoney(cartSubtotal);
    checkoutPriceElement.setAttribute('data-cart-checkout-price', cartSubtotal);
  }

  // ============================================================
  // Actualización del DOM del carrito (drawer)
  // ============================================================

  function updateCartSections(html) {
    var parser = new DOMParser();
    var htmlDoc = parser.parseFromString(html, 'text/html');

    var map = [
      ['[data-api-line-items]', '[data-items-holder]'],
      ['[data-api-upsell-items]', '[data-upsell-items]'],
      ['[data-api-cart-price]', '[data-cart-price-holder]']
    ];
    map.forEach(function (pair) {
      var incoming = htmlDoc.querySelector(pair[0]);
      var current = document.querySelector(pair[1]);
      if (incoming && current) current.innerHTML = incoming.innerHTML;
    });

    if (window.theme && window.theme.initAnimations) window.theme.initAnimations();
    rebindCartEventListeners();
  }

  function rebindCartEventListeners() {
    document.querySelectorAll('[data-item]').forEach(function (item) {
      item.addEventListener('theme:cart:update', handleCartItemUpdate);
    });
    document.querySelectorAll('[data-item-remove]').forEach(function (button) {
      button.addEventListener('click', handleCartItemRemove);
    });
  }

  function handleCartItemUpdate(event) {
    document.dispatchEvent(new CustomEvent('theme:cart:item:update', {
      bubbles: true,
      detail: { id: event.detail.id, quantity: event.detail.quantity, item: event.currentTarget }
    }));
  }

  function handleCartItemRemove(event) {
    event.preventDefault();
    var button = event.currentTarget;
    if (button.classList.contains('is-disabled')) return;
    document.dispatchEvent(new CustomEvent('theme:cart:item:update', {
      bubbles: true,
      detail: { id: button.dataset.id, quantity: 0, item: button.closest('[data-item]') }
    }));
  }

  function lockGiftQuantityInputs() {
    document.querySelectorAll('[data-item]').forEach(function (item) {
      var isGift = item.dataset.itemGift === 'true' ||
        item.classList.contains('gift') ||
        item.querySelector('[data-gift-item]') !== null;
      if (!isGift) return;

      item.querySelectorAll('.cart__quantity-field, [data-quantity-input], input[type="number"]').forEach(function (input) {
        input.setAttribute('disabled', 'disabled');
        input.setAttribute('readonly', 'readonly');
      });
      item.querySelectorAll('.cart__quantity-minus, .cart__quantity-plus, [data-quantity-minus], [data-quantity-plus]').forEach(function (btn) {
        btn.setAttribute('disabled', 'disabled');
        btn.style.pointerEvents = 'none';
        btn.style.opacity = '0.4';
      });
    });
  }

  async function refreshCartSections() {
    try {
      var cart = await getCart(true);
      var response = await fetch(ROOT + '?sections=api-cart-items');
      var data = await response.json();
      if (data && data['api-cart-items']) {
        updateCartSections(data['api-cart-items']);
        lockGiftQuantityInputs();
        renderThresholdChooserInLine();
      }
      // Estado vacío del drawer + contador (para que se limpie al vaciarse).
      var drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.classList.toggle('is-empty', cart.item_count === 0);
      if (cart.item_count === 0) {
        var holder = document.querySelector('[data-items-holder]');
        if (holder) holder.innerHTML = ''; // sin markup viejo residual
      }
      updateCartCount(cart.item_count);
    } catch (error) {
      /* noop */
    }
  }

  // Acción posterior a una mutación del carrito, según contexto.
  async function afterMutation() {
    invalidateCart();
    if (IS_CART_PAGE) {
      window.location.reload();
      return;
    }
    await refreshCartSections();
    syncCheckoutPrice();
  }

  // Renderiza directo desde la respuesta de cart/add|update (pedida con
  // sections:'api-cart-items') -> evita el getCart + ?sections extra (más rápido).
  function applyCartResponse(data) {
    if (data && data.sections && data.sections['api-cart-items']) {
      updateCartSections(data.sections['api-cart-items']);
      lockGiftQuantityInputs();
      renderThresholdChooserInLine();
    }
    // item_count viene en cart/update; en cart/add no -> derivamos del DOM.
    var count = (data && data.item_count !== undefined && data.item_count !== null) ? data.item_count : null;
    var holder = document.querySelector('[data-items-holder]');
    var empty = count !== null ? (count === 0) : !(holder && holder.querySelector('.cart__item'));
    if (empty && holder) holder.innerHTML = '';
    var drawer = document.querySelector('cart-drawer');
    if (drawer) drawer.classList.toggle('is-empty', empty);
    if (count !== null) updateCartCount(count);
    syncCheckoutPrice();
  }

  // ============================================================
  // Mutaciones del carrito
  // ============================================================

  async function removeAllGifts() {
    var cart = await getCart();
    var gifts = giftsInCart(cart);
    if (gifts.length === 0) return { removed: 0 };

    var updates = {};
    gifts.forEach(function (g) { updates[g.key] = 0; });

    await fetch(ROOT + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates })
    });
    invalidateCart();
    return { removed: gifts.length };
  }

  // Elimina líneas de regalo específicas por su key.
  async function removeGiftKeys(keys) {
    if (!keys || !keys.length) return;
    var updates = {};
    keys.forEach(function (k) { updates[k] = 0; });
    await fetch(ROOT + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates: updates })
    });
    invalidateCart();
  }

  // Remoción atómica de un pañuelo + sus bandanas (una sola operación).
  async function atomicRemove(updates) {
    try {
      var res = await cartPost(ROOT + 'cart/update.js', { updates: updates, sections: 'api-cart-items' });
      var data = await res.json();
      invalidateCart();
      if (IS_CART_PAGE) { window.location.reload(); return; }
      applyCartResponse(data); // render inmediato desde la respuesta
      // Rebalancear el tope (p.ej. al quitar un pañuelo se libera cupo para otro).
      await reconcileAutomaticGifts(await getCart());
      await updateGiftProgress(await getCart());
    } catch (e) {
      /* si falla, no bloqueamos la UI */
    }
  }

  var isAddingGift = false;
  async function addGift(giftProduct, discountCode, giftConfig) {
    if (isAddingGift) return { success: false, busy: true };
    isAddingGift = true;
    try {
      var cart = await getCart(true);
      var existing = giftsInCart(cart);

      // Ya hay un regalo del mismo código -> no duplicar.
      var sameCode = existing.some(function (g) {
        return g.properties._discount_code &&
          g.properties._discount_code.toUpperCase() === discountCode.toUpperCase();
      });
      if (sameCode) return { success: true, alreadyAdded: true };

      // Revalidar contra el carrito más reciente.
      var q = await cartQualifies(cart, giftConfig);
      if (!q.ok) return { success: false, cancelled: true };

      var response = await fetch(ROOT + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          items: [{
            id: giftProduct.variantId,
            quantity: 1,
            properties: {
              _gift: 'true',
              _discount_code: discountCode,
              _gift_name: giftProduct.productTitle
            }
          }]
        })
      });
      var data = await response.json();
      if (data.status === 422 || data.status === 404 || data.message) {
        return { success: false, error: 'stock', message: data.description || data.message };
      }

      invalidateCart();
      await afterMutation();
      return { success: true };
    } catch (error) {
      return { success: false, error: 'network' };
    } finally {
      isAddingGift = false;
    }
  }

  // Elimina regalos añadidos manualmente / duplicados y fuerza qty=1.
  async function normalizeCartGifts() {
    var cart = await getCart();

    var giftVariantIds = new Set();
    Object.keys(GIFTS).forEach(function (code) {
      (GIFTS[code].products || []).forEach(function (p) {
        if (p.variantId) giftVariantIds.add(p.variantId);
      });
    });
    if (giftVariantIds.size === 0) return { changed: false };

    var updates = {};
    var changed = false;

    cart.items.forEach(function (item) {
      // Solo eliminar variantes de regalo agregadas manualmente (sin _gift).
      // La cantidad de los regalos legítimos la maneja la reconciliación.
      if (giftVariantIds.has(item.variant_id) && !isGiftItem(item)) {
        updates[item.key] = 0;
        changed = true;
      }
    });

    if (!changed) return { changed: false };

    await fetch(ROOT + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates })
    });
    invalidateCart();
    return { changed: true };
  }

  // ============================================================
  // Validaciones
  // ============================================================

  // Quita regalos que ya no cumplen requisitos. Expuesta como
  // window.validateGiftEligibility (theme.js depende de ella).
  async function validateGiftEligibility() {
    try {
      // Fresco: la llama theme.js tras cambios del carrito; el cache podría estar obsoleto.
      var cart = await getCart(true);
      var subtotal = subtotalWithoutGifts(cart);
      var gifts = giftsInCart(cart);
      if (gifts.length === 0) return { removed: false };

      // Subtotal 0 -> fuera regalos.
      if (subtotal === 0) {
        await removeAllGifts();
        await afterMutation();
        return { removed: true, reason: 'subtotal_zero' };
      }

      // Solo validamos regalos de CUPÓN aquí. Los automáticos (bandanas) los
      // gestiona reconcileAutomaticGifts (cantidad según pañuelos).
      var toRemove = [];
      for (var i = 0; i < gifts.length; i++) {
        var code = gifts[i].properties._discount_code;
        if (!code) continue;
        var cfg = GIFTS[code.toUpperCase()] || GIFTS[code];

        if (cfg && cfg.regaloAutomatico) continue; // lo maneja la reconciliación

        if (!cfg) {
          toRemove.push(gifts[i].key); // código inexistente
          continue;
        }

        var q = await cartQualifies(cart, cfg);
        if (!q.ok) toRemove.push(gifts[i].key);
      }

      if (toRemove.length) {
        await removeGiftKeys(toRemove);
        await afterMutation();
        return { removed: true, reason: 'coupon_ineligible' };
      }
      return { removed: false, subtotal: subtotal };
    } catch (error) {
      return { removed: false, error: error };
    }
  }

  // Limpia el código guardado si fue removido del checkout.
  async function validateSavedDiscountCode() {
    var savedCode = sessionStorage.getItem('shopify_discount_code');
    if (!savedCode) return;

    try {
      var cart = await getCart();
      if (GIFTS[savedCode]) return; // código de regalo: válido mientras exista

      var applied = cart.cart_level_discount_applications &&
        cart.cart_level_discount_applications.some(function (d) {
          return d.title.toUpperCase() === savedCode.toUpperCase();
        });

      if (!applied) {
        sessionStorage.removeItem('shopify_discount_code');
        var input = document.querySelector('[data-discount-input]');
        if (input) input.value = '';
        var message = document.querySelector('[data-discount-message]');
        if (message) message.style.display = 'none';
      }
    } catch (error) {
      /* noop */
    }
  }

  // Añade el regalo si hay un código guardado que ahora sí califica.
  var isCheckingAutoGift = false;
  async function autoAddGiftIfEligible() {
    if (isCheckingAutoGift) return;
    var savedCode = sessionStorage.getItem('shopify_discount_code');
    if (!savedCode) return;
    var cfg = GIFTS[savedCode];
    if (!cfg) return;

    isCheckingAutoGift = true;
    try {
      var cart = await getCart();
      var alreadyHas = giftsInCart(cart).some(function (g) {
        return g.properties._discount_code === savedCode;
      });
      if (alreadyHas) {
        showDiscountMessage(MSG.appliedWithGift, 'success');
        return;
      }

      var q = await cartQualifies(cart, cfg);
      if (!q.ok) {
        if (q.reason === 'min_amount') {
          showDiscountMessage(MSG.minAmountRequired.replace('[MONTO]', formatMoney(q.remaining)), 'warning');
        } else if (q.reason === 'min_quantity') {
          showDiscountMessage(MSG.minItemsRequired.replace('[CANTIDAD]', q.remaining), 'warning');
        } else if (q.reason === 'category' || q.reason === 'specific_product') {
          showDiscountMessage(MSG.giftNotApplicable, 'warning');
        }
        return;
      }

      var product = getNextGiftProduct(cfg.products, savedCode);
      if (!product || !canAddGift(product)) {
        showDiscountMessage(MSG.noGiftsAvailable, 'warning');
        return;
      }

      var result = await addGift(product, savedCode, cfg);
      if (result.success && !result.alreadyAdded) {
        showDiscountMessage(MSG.appliedWithGift, 'success');
      }
    } catch (error) {
      /* noop */
    } finally {
      isCheckingAutoGift = false;
    }
  }

  // Regalos automáticos (regalo_automatico = true): sin cupón, en cuanto el
  // carrito contiene un producto de la lista `productos_especificos`.
  // Tope global de bandanas de regalo (automáticas).
  var AUTO_GIFT_CAP = 3;

  // Devuelve el código de la promo automática cuyo `productos_especificos`
  // incluye ese handle (p. ej. handle de pañuelo -> promo de su bandana).
  function findAutomaticPromoForHandle(handle) {
    var codes = Object.keys(GIFTS);
    for (var i = 0; i < codes.length; i++) {
      var cfg = GIFTS[codes[i]];
      if (cfg && cfg.regaloAutomatico &&
          itemMatchesAnySpecificProduct(handle, cfg.productosEspecificos)) {
        return codes[i];
      }
    }
    return null;
  }

  // Reconcilia los regalos AUTOMÁTICOS: 1 bandana por cada unidad de su pañuelo,
  // con tope global AUTO_GIFT_CAP recorriendo el carrito en orden. La bandana
  // queda emparejada al pañuelo (propiedad _gift_for) para anidarla en el carrito.
  async function reconcileAutomaticGifts(cart) {
    // 1) Cantidad deseada por código (en orden del carrito, hasta el tope).
    var desired = {};
    var giftForByCode = {}; // code -> handle del pañuelo real que la origina
    var remaining = AUTO_GIFT_CAP;
    cart.items.forEach(function (item) {
      if (remaining <= 0 || isGiftItem(item)) return;
      var code = findAutomaticPromoForHandle(item.handle);
      if (!code) return;
      var take = Math.min(item.quantity, remaining);
      desired[code] = (desired[code] || 0) + take;
      if (!giftForByCode[code]) giftForByCode[code] = item.handle;
      remaining -= take;
    });

    // Limitar por stock de la bandana (si no permite sobreventa).
    Object.keys(desired).forEach(function (code) {
      var p = (GIFTS[code].products || [])[0];
      if (p && p.inventoryPolicy !== 'continue') {
        desired[code] = Math.min(desired[code], p.inventoryQuantity || 0);
      }
      if (desired[code] <= 0) delete desired[code];
    });

    // 2) Líneas de regalo automático actuales por código.
    var currentByCode = {};
    cart.items.forEach(function (item) {
      if (!isGiftItem(item)) return;
      var code = item.properties._discount_code;
      if (!code || !GIFTS[code] || !GIFTS[code].regaloAutomatico) return;
      if (!currentByCode[code]) currentByCode[code] = { key: item.key, quantity: item.quantity, extraKeys: [] };
      else { currentByCode[code].extraKeys.push(item.key); currentByCode[code].quantity += item.quantity; }
    });

    // 3) Construir updates (líneas existentes) y adds (códigos nuevos).
    var updates = {};
    var adds = [];
    var changed = false;

    Object.keys(currentByCode).forEach(function (code) {
      var cur = currentByCode[code];
      var want = desired[code] || 0;
      cur.extraKeys.forEach(function (k) { updates[k] = 0; changed = true; }); // colapsar duplicados
      if (cur.quantity !== want || cur.extraKeys.length) { updates[cur.key] = want; changed = true; }
      delete desired[code];
    });

    Object.keys(desired).forEach(function (code) {
      var p = (GIFTS[code].products || [])[0];
      if (!p) return;
      adds.push({
        id: p.variantId,
        quantity: desired[code],
        properties: {
          _gift: 'true',
          _discount_code: code,
          _gift_name: p.productTitle,
          _gift_for: giftForByCode[code] || (GIFTS[code].productosEspecificos || [])[0] || ''
        }
      });
      changed = true;
    });

    if (!changed) return { changed: false };

    try {
      var lastData = null;
      if (Object.keys(updates).length) {
        var ru = await cartPost(ROOT + 'cart/update.js', { updates: updates, sections: 'api-cart-items' });
        lastData = await ru.json();
      }
      if (adds.length) {
        var ra = await cartPost(ROOT + 'cart/add.js', { items: adds, sections: 'api-cart-items' });
        lastData = await ra.json();
      }
      invalidateCart();
      if (IS_CART_PAGE) { window.location.reload(); return { changed: true }; }
      applyCartResponse(lastData); // render desde la respuesta, sin re-fetch
      return { changed: true };
    } catch (e) {
      return { changed: false, error: e };
    }
  }

  // ============================================================
  // Aplicar código de descuento (input — solo drawer)
  // ============================================================

  async function applyDiscountCode() {
    var input = document.querySelector('[data-discount-input]');
    var applyButton = document.querySelector('[data-discount-apply]');
    var discountCode = (input && input.value.trim().toUpperCase()) || '';

    if (!discountCode) {
      showDiscountMessage(MSG.codeEmpty, 'error');
      return;
    }
    if (applyButton) applyButton.disabled = true;
    if (input) input.disabled = true;

    try {
      var cfg = GIFTS[discountCode];

      // --- Código de regalo: se procesa en el front, NO se manda a Shopify ---
      if (cfg && cfg.active && cfg.products && cfg.products.length > 0) {
        sessionStorage.setItem('shopify_discount_code', discountCode);
        invalidateCart();
        await validateGiftEligibility();

        var cart = await getCart(true);
        var q = await cartQualifies(cart, cfg);
        if (!q.ok) {
          if (q.reason === 'min_amount') {
            showDiscountMessage(MSG.minAmountRequired.replace('[MONTO]', formatMoney(q.remaining)), 'warning');
          } else if (q.reason === 'min_quantity') {
            showDiscountMessage(MSG.minItemsRequired.replace('[CANTIDAD]', q.remaining), 'warning');
          } else {
            showDiscountMessage(MSG.giftNotApplicable, 'warning');
          }
          return;
        }

        var product = getNextGiftProduct(cfg.products, discountCode);
        if (!product) { showDiscountMessage(MSG.noGiftsAvailable, 'warning'); return; }
        if (!canAddGift(product)) { showDiscountMessage(MSG.noGiftStock, 'warning'); return; }

        var result = await addGift(product, discountCode, cfg);
        if (result.success) {
          showDiscountMessage(result.alreadyAdded ? MSG.codeApplied : MSG.appliedWithGift, 'success');
        } else if (result.error === 'stock') {
          showDiscountMessage(MSG.giftUnavailable, 'warning');
        } else {
          showDiscountMessage(MSG.codeApplied, 'success');
        }
        return;
      }

      // --- Código normal de Shopify ---
      var discountResponse = await fetch(ROOT + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ discount: discountCode, sections: 'api-cart-items' })
      });
      var cartData = await discountResponse.json();
      invalidateCart();

      var hasMonetaryDiscount = cartData.cart_level_discount_applications &&
        cartData.cart_level_discount_applications.some(function (d) {
          return d.title.toUpperCase() === discountCode;
        });

      if (hasMonetaryDiscount) {
        sessionStorage.setItem('shopify_discount_code', discountCode);
        if (cartData.sections && cartData.sections['api-cart-items']) {
          updateCartSections(cartData.sections['api-cart-items']);
        }
        if (cartData.item_count !== undefined) updateCartCount(cartData.item_count);
        syncCheckoutPrice();
        await validateGiftEligibility();
        showDiscountMessage(MSG.codeApplied, 'success');
      } else {
        sessionStorage.removeItem('shopify_discount_code');
        showDiscountMessage(MSG.codeInvalid, 'error');
      }
    } catch (error) {
      showDiscountMessage(MSG.codeError, 'error');
    } finally {
      if (applyButton) applyButton.disabled = false;
      if (input) input.disabled = false;
    }
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.setAttribute('data-cart-count', count);
    });
  }

  // ============================================================
  // Orquestador con control de concurrencia
  // ============================================================

  var isProcessing = false;
  var pendingUpdate = null;

  async function processCartUpdate(source) {
    if (isProcessing) { pendingUpdate = source || 'unknown'; return; }
    isProcessing = true;
    invalidateCart();

    try {
      syncCheckoutPrice();
      var cart = await getCart();
      await updateGiftProgress(cart);

      // PRIORIDAD: agregar/ajustar las bandanas cuanto antes, para que el regalo
      // aparezca rápido. Las validaciones de cupón/limpieza van después y no
      // bloquean la aparición del regalo.
      await reconcileAutomaticGifts(cart);

      // En /cart, cualquier mutación llama afterMutation() -> recarga y navega fuera,
      // cortando naturalmente la secuencia. En el drawer refresca y continúa.
      var norm = await normalizeCartGifts();
      if (norm.changed) await afterMutation();

      await validateSavedDiscountCode();
      await validateGiftEligibility();
      await autoAddGiftIfEligible();

      syncCheckoutPrice();
      var finalCart = await getCart();
      await updateGiftProgress(finalCart);
      await reconcileThresholdGift(finalCart);
    } catch (error) {
      /* noop */
    } finally {
      isProcessing = false;
      if (pendingUpdate) {
        var pending = pendingUpdate;
        pendingUpdate = null;
        setTimeout(function () { processCartUpdate(pending + '_pending'); }, 100);
      }
    }
  }

  function handleCartUpdate(source, delay) {
    setTimeout(function () { processCartUpdate(source); }, delay == null ? 300 : delay);
  }

  // ============================================================
  // Exponer lo que el tema / el markup necesitan globalmente
  // ============================================================
  window.validateGiftEligibility = validateGiftEligibility; // usada por theme.js
  window.applyDiscountCode = applyDiscountCode;              // usada por onclick del input
  window.handleCartUpdate = handleCartUpdate;
  window.updateCheckoutButtonPrice = syncCheckoutPrice;

  // ============================================================
  // Inicialización
  // ============================================================
  function init() {
    setTimeout(function () { processCartUpdate('page_load'); }, 500);

    // Input de cupón (solo existe en el drawer).
    var discountInput = document.querySelector('[data-discount-input]');
    if (discountInput) {
      discountInput.addEventListener('keypress', function (e) {
        if (e.key === 'Enter') { e.preventDefault(); applyDiscountCode(); }
      });
      var savedCode = sessionStorage.getItem('shopify_discount_code');
      if (savedCode) discountInput.value = savedCode;

      // Redirigir a checkout con el código guardado (comportamiento del drawer).
      var checkoutForm = document.querySelector('[data-cart-form]');
      if (checkoutForm) {
        checkoutForm.addEventListener('submit', function (e) {
          var code = sessionStorage.getItem('shopify_discount_code');
          if (code) {
            e.preventDefault();
            window.location.href = '/checkout?discount=' + encodeURIComponent(code);
          }
        });
      }
    }

    // Revalidar al abrir el drawer.
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          if (m.attributeName === 'open' && cartDrawer.hasAttribute('open')) {
            processCartUpdate('drawer_opened');
          }
        });
      });
      observer.observe(cartDrawer, { attributes: true });
    }

    // Eventos del carrito del tema.
    document.addEventListener('theme:cart:change', function () { processCartUpdate('theme_cart_change'); });
    document.addEventListener('theme:cart:item:update', function () { handleCartUpdate('item_update', 50); });
    document.addEventListener('theme:product:added', function () { handleCartUpdate('product_added', 50); });

    // Remoción atómica: al quitar un pañuelo con bandana(s) de regalo, se quitan
    // pañuelo + bandana(s) en UNA sola operación -> evita el estado "huérfano"
    // intermedio y el parpadeo/lag. Decisión SÍNCRONA leyendo el .cart__item__gift
    // anidado en el DOM (data-nested-gift lleva la key de cada bandana).
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-item-remove], .cart__item__remove');
      if (!btn) return;
      var itemEl = btn.closest('[data-item]');
      if (!itemEl) return;
      var nested = itemEl.querySelectorAll('[data-nested-gift]');
      if (!nested.length) return; // sin bandana -> flujo normal del tema
      e.preventDefault();
      e.stopImmediatePropagation();
      var updates = {};
      updates[itemEl.getAttribute('data-item')] = 0;
      nested.forEach(function (n) { updates[n.getAttribute('data-nested-gift')] = 0; });
      atomicRemove(updates);
    }, true);

    // Impedir cambios de cantidad en regalos.
    document.addEventListener('click', function (e) {
      var giftItem = e.target.closest('.cart__item.gift');
      if (giftItem && (e.target.closest('.cart__quantity-minus') || e.target.closest('.cart__quantity-plus'))) {
        e.preventDefault();
        e.stopPropagation();
      }
    });
    document.addEventListener('input', function (e) {
      if (e.target.classList.contains('cart__quantity-field') && e.target.closest('.cart__item.gift')) {
        e.target.value = 1;
      }
    });

    // Select visual del regalo por umbral: abrir/cerrar + elegir color.
    document.addEventListener('click', function (e) {
      // Abrir/cerrar el desplegable.
      var toggle = e.target.closest('[data-gt-select-toggle]');
      if (toggle) {
        e.preventDefault();
        var wrap = toggle.closest('[data-gt-select]');
        var menu = wrap && wrap.querySelector('[data-gt-select-menu]');
        var willOpen = menu && menu.hasAttribute('hidden');
        document.querySelectorAll('[data-gt-select-menu]').forEach(function (m) { m.setAttribute('hidden', ''); });
        document.querySelectorAll('[data-gt-select-toggle]').forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
        if (willOpen) { menu.removeAttribute('hidden'); toggle.setAttribute('aria-expanded', 'true'); }
        return;
      }
      // Elegir una opción en stock.
      var opt = e.target.closest('[data-gift-choose]');
      if (opt && !opt.disabled && opt.getAttribute('data-available') === 'true') {
        var vid = parseInt(opt.getAttribute('data-variant-id'), 10);
        var menu2 = opt.closest('[data-gt-select-menu]');
        if (menu2) {
          menu2.setAttribute('hidden', '');
          var tog = menu2.parentElement.querySelector('[data-gt-select-toggle]');
          if (tog) tog.setAttribute('aria-expanded', 'false');
        }
        if (vid) { markSelectedThresholdOption(vid); chooseThresholdGift(vid); }
        return;
      }
      // Click fuera del select -> cerrar.
      if (!e.target.closest('[data-gt-select]')) {
        document.querySelectorAll('[data-gt-select-menu]').forEach(function (m) { m.setAttribute('hidden', ''); });
        document.querySelectorAll('[data-gt-select-toggle]').forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      }
    });

    // Vigilancia ligera: lee el subtotal del DOM (sin fetch). Si es 0, valida.
    setInterval(function () {
      var el = document.querySelector('[data-cart-total]');
      if (!el) return;
      var subtotal = parseInt(el.getAttribute('data-cart-total'), 10) || 0;
      if (subtotal === 0) processCartUpdate('periodic_zero');
    }, 5000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
